import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useChunks, useUpdateChunk } from '@/hooks/useChunks'
import { Search, ChevronLeft, ChevronRight, Edit2, Save, X, Loader2 } from 'lucide-react'
import { useDebouncedValue } from '@/hooks/useDebounce'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface ChunksDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  kbId: string
  documentId: string
  documentName: string
  documentStatus: string
}

export function ChunksDialog({ 
  open, 
  onOpenChange, 
  kbId, 
  documentId, 
  documentName,
  documentStatus
}: ChunksDialogProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [editingChunkId, setEditingChunkId] = useState<string | null>(null)
  const [editContent, setEditContent] = useState('')
  const [editKeywords, setEditKeywords] = useState('')
  const pageSize = 10

  // Debounce search query
  const debouncedSearch = useDebouncedValue(searchQuery, 500)

  // Fetch chunks
  const { data, isLoading, error } = useChunks(
    kbId,
    documentId,
    currentPage,
    pageSize,
    debouncedSearch || undefined
  )

  // Update mutation
  const updateMutation = useUpdateChunk(kbId, documentId)

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch])

  // Check if document is parsed
  const isDocumentParsed = documentStatus === 'DONE'

  if (!isDocumentParsed) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{documentName}</DialogTitle>
            <DialogDescription>View and edit document chunks</DialogDescription>
          </DialogHeader>
          <Alert>
            <AlertDescription>
              This document has not been parsed yet. Please parse the document first to view chunks.
            </AlertDescription>
          </Alert>
        </DialogContent>
      </Dialog>
    )
  }

  const handleEdit = (chunkId: string, content: string, keywords: string) => {
    setEditingChunkId(chunkId)
    setEditContent(content)
    setEditKeywords(keywords)
  }

  const handleCancelEdit = () => {
    setEditingChunkId(null)
    setEditContent('')
    setEditKeywords('')
  }

  const handleSave = async (chunkId: string) => {
    try {
      await updateMutation.mutateAsync({
        chunkId,
        content: editContent,
        important_keywords: editKeywords
      })
      setEditingChunkId(null)
    } catch (error) {
      console.error('Failed to update chunk:', error)
    }
  }

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{documentName}</DialogTitle>
          <DialogDescription>
            {data ? `${data.total} chunks` : 'Loading chunks...'}
          </DialogDescription>
        </DialogHeader>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search chunks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        

        {/* Chunks List */}
        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>Failed to load chunks. Please try again.</AlertDescription>
            </Alert>
          )}

          {data && data.chunks.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No chunks found.
            </div>
          )}

          {data && data.chunks.map((chunk, index) => {
            const isEditing = editingChunkId === chunk.id
            const chunkNumber = (currentPage - 1) * pageSize + index + 1

            return (
              <div key={chunk.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">
                      Chunk {chunkNumber}
                    </span>
                    <Badge 
                      variant={chunk.available ? 'default' : 'destructive'}
                      className={chunk.available ? 'bg-green-100 text-green-800 border-green-200' : ''}
                    >
                      {chunk.available ? 'Available' : 'Not Available'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isEditing && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(
                          chunk.id, 
                          chunk.content, 
                          Array.isArray(chunk.important_keywords) 
                            ? chunk.important_keywords.join(', ') 
                            : chunk.important_keywords || ''
                        )}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    )}
                    {isEditing && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleCancelEdit}
                          disabled={updateMutation.isPending}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleSave(chunk.id)}
                          disabled={updateMutation.isPending}
                        >
                          {updateMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Save className="h-4 w-4" />
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div>
                  <label className="text-sm font-medium">Content:</label>
                  {isEditing ? (
                    <Textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="mt-1 min-h-[100px]"
                      disabled={updateMutation.isPending}
                    />
                  ) : (
                    <div className="mt-1 text-sm prose prose-sm max-w-none">
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({node, ...props}) => <h1 className="text-xl font-bold mt-4 mb-2" {...props} />,
                          h2: ({node, ...props}) => <h2 className="text-lg font-bold mt-3 mb-2" {...props} />,
                          h3: ({node, ...props}) => <h3 className="text-base font-bold mt-2 mb-1" {...props} />,
                          p: ({node, ...props}) => <p className="mb-2" {...props} />,
                          ul: ({node, ...props}) => <ul className="list-disc list-inside mb-2" {...props} />,
                          ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-2" {...props} />,
                          li: ({node, ...props}) => <li className="ml-2" {...props} />,
                          a: ({node, ...props}) => <a className="text-blue-600 hover:underline" {...props} />,
                          code: ({node, inline, ...props}: any) => 
                            inline ? (
                              <code className="bg-gray-100 px-1 py-0.5 rounded text-xs" {...props} />
                            ) : (
                              <code className="block bg-gray-100 p-2 rounded text-xs overflow-x-auto" {...props} />
                            ),
                          blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-gray-300 pl-4 italic" {...props} />,
                        }}
                      >
                        {chunk.content}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>

                {/* Important Keywords */}
                {(chunk.important_keywords || isEditing) && (
                  <div>
                    <label className="text-sm font-medium">Important Keywords:</label>
                    {isEditing ? (
                      <Input
                        value={editKeywords}
                        onChange={(e) => setEditKeywords(e.target.value)}
                        placeholder="Enter keywords..."
                        className="mt-1"
                        disabled={updateMutation.isPending}
                      />
                    ) : (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {Array.isArray(chunk.important_keywords) ? (
                          chunk.important_keywords.length > 0 ? (
                            chunk.important_keywords.map((keyword: string, idx: number) => (
                              <Badge 
                                key={idx} 
                                variant="secondary"
                                className="bg-blue-50 text-blue-700 border-blue-200"
                              >
                                {keyword.trim()}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-sm text-muted-foreground">None</span>
                          )
                        ) : chunk.important_keywords ? (
                          <Badge 
                            variant="secondary"
                            className="bg-blue-50 text-blue-700 border-blue-200"
                          >
                            {chunk.important_keywords}
                          </Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">None</span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Questions */}
                {chunk.questions && Array.isArray(chunk.questions) && chunk.questions.length > 0 && (
                  <div>
                    <label className="text-sm font-medium">Generated Questions:</label>
                    <ul className="mt-2 space-y-2 text-sm text-muted-foreground list-disc list-inside">
                      {chunk.questions.map((question: string, idx: number) => (
                        <li key={idx} className="leading-relaxed">
                          {question}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Pagination */}
        {data && data.total > pageSize && (
          <div className="flex items-center justify-between border-t pt-4">
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages} ({data.total} total)
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1 || isLoading}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || isLoading}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
